import React, { useMemo } from "react";
import Table from "../../../common/table/index.js";
import ChangeIndicator from "../../../common/change-indicator/change-indicator.js";
import Text from "../../../common/typography/text.js";
import {
  formatWholeCurrency,
  formatUOM,
  formatGrowth,
  growthVariant,
} from "../aabiFormatters";
import { MEASURE_LABELS } from "../aabiConstants";

const GrowthCell = ({ value }) => (
  <ChangeIndicator variant={growthVariant(value)} percent={formatGrowth(value)} />
);

// The Data view of the drill explorer (§7.2). Reuses common/table — row click
// advances the scope; leaf rows now SELECT the product (§8.3: product is a
// selectable final scope), handled by the parent's drillInto.
//
// Rows arrive from buildPortfolioRows (§9.2), so this table reads the
// view-model fields — `selectedRevenue`, `selectedShare` and the flattened
// scalar `growth` — never the raw payload keys. Reading `shareOfParentRevenue`
// here would show Current-Sales shares while the Revenue column showed
// Realized values, which is exactly the disagreement the view model exists to
// prevent (§6.2, §8.1).
const DrillLevelTable = ({ rows, measure, loading, onRowClicked }) => {
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        cellRenderer: (params) =>
          params.data?.code ? `${params.data.name} (${params.data.code})` : params.value,
      },
      {
        field: "selectedRevenue",
        // The column follows the active measure — "Revenue" would contradict
        // the toolbar and the KPI strip in Realized mode.
        headerName: MEASURE_LABELS[measure] || "Revenue",
        cellRenderer: (params) => formatWholeCurrency(params.value),
      },
      {
        field: "selectedShare",
        headerName: "Share",
        cellRenderer: (params) =>
          `${((params.value ?? 0) * 100).toFixed(1)}%`,
      },
      {
        field: "uom",
        headerName: "UOM",
        cellRenderer: (params) => formatUOM(params.value),
      },
      {
        field: "growth",
        headerName: "Growth",
        sortable: false,
        cellRenderer: (params) =>
          params.data ? <GrowthCell value={params.data.growth} /> : null,
      },
      {
        field: "invoiceCount",
        headerName: "Invoices",
      },
      {
        field: "accountCount",
        headerName: "Accounts",
      },
    ],
    [measure],
  );

  return (
    <>
      {!loading && rows.length === 0 ? (
        <div className="aabi-drill-table-empty">
          <Text variant="bodyText" color="lightText">
            No activity in this period
          </Text>
        </div>
      ) : (
        <Table
          rowData={rows}
          columnDefs={columnDefs}
          loading={loading}
          screen="AABIDrillLevel"
          onRowClicked={(params) => {
            if (params.data) onRowClicked?.(params.data);
          }}
        />
      )}
    </>
  );
};

export default DrillLevelTable;
